<template>
  <div class="max-width">
    <v-row>
      <v-col cols="12" align="center" justify="center">
        <v-row>
          <v-col cols="12">
            <h2 align="center" justify="center">1-on-1 Offboarding</h2>
          </v-col>
        </v-row>
        <v-data-table
          height="600"
          :headers="table.headers"
          :options="table.options"
          :items="subscriptions"
          :item-class="rowClass"
          :search="search"
          :show-group-by="true"
          :hide-default-footer="true"
          disable-pagination
          fixed-header
          dense
          :loading="$apollo.loading"
        >
          <template v-slot:top>
            <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
              <template v-slot:label>
                <v-icon color="secondary"> mdi-magnify </v-icon>
                Search
              </template>
            </v-text-field>
          </template>

          <template v-slot:[`item.profile_pic`]="props">
            <profile-pic :profilePic="props.item.profile_pic" :userId="props.item.userId" />
          </template>

          <template v-slot:[`header.client_name`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.client_name`]="props">
            <client-name
              :client-id="props.item.userId"
              :dob="props.item.dateOfBirth"
              :profileLink="props.item.profile_link"
              :subscriptionEndDate="props.item.subscriptionEndDate"
              :clientName="props.item.client_name"
              :username="props.item.username"
              :contactLink="props.item.contact_link"
              :lastActive="props.item.client.userProfile.lastActive"
              :nextSubscription="props.item.nextSubscription"
              :coachingChat="props.item.client.userProfile.coachingChat"
              :supportChat="props.item.client.userProfile.supportChat"
              :coachId="coachId"
              :subscriptionSurveyId="props.item.subscriptionSurveyId"
            />
          </template>

          <template v-slot:[`header.endDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.endDate`]="props">
            <span>{{ props.item.endDate | moment("ddd, DD MMMM YYYY") }} </span>
            <v-tooltip v-if="isTomorrow(props.item.endDate)" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="red" dark v-bind="attrs" v-on="on">
                  mdi-alert-circle-outline
                </v-icon>
              </template>
              <span>This client ends tomorrow</span>
            </v-tooltip>
            <v-tooltip v-else-if="props.item.ended" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="grey darken-1" dark v-bind="attrs" v-on="on">
                  mdi-alert-circle-outline
                </v-icon>
              </template>
              <span>This client is no longer active on this subscription</span>
            </v-tooltip>
          </template>

          <template v-slot:[`header.finalCheckinDone`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" required />
          </template>
          <template v-slot:[`item.finalCheckinDone`]="props">
            <v-simple-checkbox
              color="secondary"
              class="mt-0"
              :ripple="false"
              :value="props.item.finalCheckinDone"
              @input="
                props.item.finalCheckinDone = !props.item.finalCheckinDone;
                updateCheck(props.item, props.header.type, props.item.finalCheckinDone);
              "
            />
          </template>

          <template v-slot:[`header.continuingWithJeff`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.continuingWithJeff`]="props">
            <span :class="{ 'red--text font-weight-bold': props.item.continuingWithJeff == 'No' }">
              {{ props.item.continuingWithJeff }}</span
            >
          </template>

          <template v-slot:[`header.subscriptionDuration`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>

          <template v-slot:[`header.likelyToReturnOneOnOne`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.likelyToReturnOneOnOne`]="props">
            <v-simple-checkbox
              color="secondary"
              class="mt-0"
              :ripple="false"
              :value="props.item.likelyToReturnOneOnOne"
              @input="
                props.item.likelyToReturnOneOnOne = !props.item.likelyToReturnOneOnOne;
                updateCheck(props.item, props.header.type, props.item.likelyToReturnOneOnOne);
              "
            />
          </template>

          <template v-slot:[`header.clientRemovedMainWaGroup`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" required />
          </template>
          <template v-slot:[`item.clientRemovedMainWaGroup`]="props">
            <v-simple-checkbox
              color="secondary"
              class="mt-0"
              :ripple="false"
              :value="props.item.clientRemovedMainWaGroup"
              @input="
                props.item.clientRemovedMainWaGroup = !props.item.clientRemovedMainWaGroup;
                updateCheck(props.item, props.header.type, props.item.clientRemovedMainWaGroup);
              "
            />
          </template>

          <template v-slot:[`header.botRemovedClientWaGroupChatArchived`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" required />
          </template>
          <template v-slot:[`item.botRemovedClientWaGroupChatArchived`]="props">
            <v-simple-checkbox
              color="secondary"
              class="mt-0"
              :ripple="false"
              :value="props.item.botRemovedClientWaGroupChatArchived"
              @input="
                props.item.botRemovedClientWaGroupChatArchived = !props.item.botRemovedClientWaGroupChatArchived;
                updateCheck(props.item, props.header.type, props.item.botRemovedClientWaGroupChatArchived);
              "
            />
          </template>

          <template v-slot:[`header.finalMessageSent`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" required />
          </template>
          <template v-slot:[`item.finalMessageSent`]="props">
            <v-simple-checkbox
              color="secondary"
              class="mt-0"
              :ripple="false"
              :value="props.item.finalMessageSent"
              @input="
                props.item.finalMessageSent = !props.item.finalMessageSent;
                updateCheck(props.item, props.header.type, props.item.finalMessageSent);
              "
            />
          </template>

          <template v-slot:[`header.notesOnWhyLeft`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" required />
          </template>
          <template v-slot:[`item.notesOnWhyLeft`]="props">
            <div v-if="focusTextId == props.item.id">
              <v-textarea
                autofocus
                outlined
                rows="2"
                placeholder="Press Enter to save; Esc to close"
                v-model="props.item.newNotes"
                auto-grow
                hide-details
                class="my-1"
                @keydown.esc="focusTextId = null"
                @keydown.enter="saveText(props.item)"
              />
              <div class="text-right my-1">
                <v-btn icon small elevation="2" color="grey darken-2" @click="focusTextId = null">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-btn icon small elevation="2" color="blue" @click="saveText(props.item)" class="mx-1">
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-else @dblclick="focusText(props.item)" class="fill-height d-flex align-center pointer">
              {{ props.item.notesOnWhyLeft }}
              <v-btn
                icon
                :x-small="props.item.notesOnWhyLeft && props.item.notesOnWhyLeft.length > 0"
                elevation="2"
                color="grey darken-2"
                @click="focusText(props.item)"
                :class="{ 'ml-2': props.item.notesOnWhyLeft && props.item.notesOnWhyLeft.length > 0 }"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:[`header.offboardingCompleted`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.offboardingCompleted`]="props">
            <div class="d-flex text-center">
              <v-tooltip v-if="!props.item.ended" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-simple-checkbox color="secondary" class="mt-0" :ripple="false" disabled v-bind="attrs" v-on="on" />
                </template>
                <span>Can only be completed once subscription has ended</span>
              </v-tooltip>
              <v-simple-checkbox
                v-else
                color="secondary"
                class="mt-0"
                :ripple="false"
                :value="props.item.offboardingCompleted"
                @input="validateCompleted(props.item)"
              />
              <v-tooltip bottom class="ml-1">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" slot="activator" class="data-table-header">
                    <v-icon size="18">mdi-help-circle-outline</v-icon>
                  </div>
                </template>
                <span>Client will be removed from dashboard</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:[`header.cancellationRequestDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.cancellationRequestDate`]="props">
            <span>{{ props.item.cancellationRequestDate | moment("ddd, DD MMMM") }} </span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { COACHING_OFFBOARDING_SUBSCRIPTION_QUERY, SET_OFFBOARDING_MUTATION } from "@/constants/graphql/coach/coaching";
import { UPSERT_SUBSCRIPTION_CHECK_MUTATION } from "@/constants/graphql/operations/onboarding";
import { getUserName } from "@/lib/username";
import CoachingDashboardBase from "../mixins/CoachingDashboardBase";
import MetricMixin from "@/components/shared/mixins/MetricMixin";

export default {
  name: "CoachingOffboarding",
  props: ["coachId"],
  mixins: [CoachingDashboardBase, MetricMixin],
  data() {
    return {
      skipDashboardListingQuery: true,
      search: "",
      focusTextId: null,
      selectedFullname: "",
      subscriptions: [],
      selectedUserId: null,
      table: {
        options: {
          showGroupBy: true,
          multiSort: false,
        },
        headers: [
          { text: "", value: "profile_pic", groupable: false, sortable: false },
          { text: "Client", value: "client_name", groupable: false },
          { text: "End date", value: "endDate", sortable: true, help: "Subscription end date" },
          {
            text: "Final check-in",
            value: "finalCheckinDone",
            sortable: false,
            help: "Final check-in completed",
            type: "final_checkin_done",
          },
          { text: "Staying with us", value: "continuingWithJeff", sortable: false, help: "Continuing" },
          {
            text: "Sub length",
            value: "subscriptionDuration",
            sortable: false,
            help: "Duration on ending subscription",
          },
          {
            text: "Would return",
            value: "likelyToReturnOneOnOne",
            sortable: false,
            help: "Likely to return to 1-on-1",
            type: "likely_to_return_one_on_one",
          },
          {
            text: "Group removed",
            value: "clientRemovedMainWaGroup",
            sortable: false,
            help: "Removed client from main WhatsApp group",
            type: "client_removed_main_wa_group",
          },
          {
            text: "Bot removed & archived",
            value: "botRemovedClientWaGroupChatArchived",
            sortable: false,
            help: "Removed Bot from client WhatsApp group and archived the chat",
            type: "bot_removed_client_wa_group_chat_archived",
          },
          {
            text: "Final message sent",
            value: "finalMessageSent",
            sortable: true,
            help: "Final goodbye message sent",
            type: "final_message_sent",
          },
          {
            text: "Notes on leaving",
            value: "notesOnWhyLeft",
            groupable: false,
            width: "200",
            help: "Notes on why client left",
          },
          { text: "Completed", value: "offboardingCompleted", sortable: true, help: "Offboarding completed" },
          { text: "Cancellation submitted", value: "cancellationRequestDate", sortable: true },
          {
            text: "",
            value: "controls",
            sortable: false,
            groupable: false,
            width: 120,
          },
        ],
      },
    };
  },
  methods: {
    focusText(item) {
      this.focusTextId = item.id;
    },
    saveText(item) {
      item.notesOnWhyLeft = item.newNotes;
      this.updateOffboarding(item);
      this.focusTextId = null;
    },
    isTomorrow(date) {
      let tomorrow = this.$moment()
        .add(1, "day")
        .format("YYYY-MM-DD");
      let endDate = this.$moment(date).format("YYYY-MM-DD");
      return tomorrow == endDate;
    },
    isPast(date) {
      return this.$moment().isAfter(this.$moment(date));
    },
    updateCheck(item, type, value) {
      this.$apollo
        .mutate({
          mutation: UPSERT_SUBSCRIPTION_CHECK_MUTATION,
          variables: {
            input: {
              subscriptionId: atob(item.id).split(":")[1],
              type: type,
              value: value,
            },
          },
          refetchQueries: ["userSubscriptions"],
        })
        .then((result) => {
          if (result.data.upsertSubscriptionCheckRecord.success) {
            this.$toasted.success(`Offboarding updated for ${item.client_name}`);
          }
        });
    },
    validateCompleted(item) {
      if (
        item.finalCheckinDone &&
        item.clientRemovedMainWaGroup &&
        item.botRemovedClientWaGroupChatArchived &&
        item.finalMessageSent &&
        item.notesOnWhyLeft.length > 0
      ) {
        item.offboardingCompleted = !item.offboardingCompleted;
        this.updateOffboarding(item);
      } else {
        this.$toasted.error(`Could not complete offboarding. ${item.client_name} still has required tasks.`);
      }
    },
    updateOffboarding(item) {
      let input = {
        subscriptionId: atob(item.id).split(":")[1],
        notesOnWhyLeft: item.notesOnWhyLeft,
        offboardingCompleted: item.offboardingCompleted,
      };

      this.$apollo
        .mutate({
          mutation: SET_OFFBOARDING_MUTATION,
          variables: {
            input: input,
          },
        })
        .then((response) => {
          if (response.data?.setOffboardingMutation?.success) {
            if (item.offboardingCompleted) {
              this.$toasted.success(`Offboarding completed for ${item.client_name}`);
              this.subscriptions = this.subscriptions.filter((s) => !s.offboardingCompleted);
            } else this.$toasted.success(`Offboarding updated for ${item.client_name}`);
          } else {
            this.emitProblem();
          }
        })
        .catch(() => {
          this.emitProblem();
        });
    },
  },
  apollo: {
    userSubscriptions: {
      query: COACHING_OFFBOARDING_SUBSCRIPTION_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.coachId);

        let input = {
          stages: ["offboarding"],
          productType: "one-on-one",
          coachId: encoded_json_payload,
          selectedDate: this.$moment().format("YYYY-MM-DD"),
        };
        return input;
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.subscriptions = response.data.userSubscriptions.edges
            .map((item) => {
              return {
                id: item.node.id,
                userId: item.node.user.id,
                profile_pic: item.node.user.userProfile.profilePicture,
                profile_link: `/client-profile/${this.coachId}/${item.node.user.id}`,
                contact_link: `/clients/${item.node.user.id}`,
                client_name: getUserName(item.node.user),
                username: item.node.user.username,
                client: item.node.user,
                dateOfBirth: item.node.user.userProfile.dateOfBirth,
                endDate: item.node.endDate,
                cancellationRequestDate: item.node.cancellationRequestDate,
                finalCheckinDone: item.node.finalCheckinDone,
                continuingWithJeff: item.node.transitionProductVariant?.product?.title ?? "No",
                subscriptionDuration: this.$moment(item.node.startDate).fromNow(true),
                likelyToReturnOneOnOne: item.node.likelyToReturnOneOnOne,
                clientRemovedMainWaGroup: item.node.clientRemovedMainWaGroup,
                botRemovedClientWaGroupChatArchived: item.node.botRemovedClientWaGroupChatArchived,
                finalMessageSent: item.node.finalMessageSent,
                notesOnWhyLeft: item.node.notesOnWhyLeft,
                newNotes: item.node.notesOnWhyLeft,
                offboardingCompleted: item.node.offboardingCompleted,
                ended: this.isPast(item.node.endDate),
              };
            })
            .sort((a, b) => {
              return this.$moment(a.endDate).isBefore(this.$moment(b.endDate)) ? -1 : 1;
            })
            .sort((a, b) => {
              return a.ended < b.ended ? -1 : 1;
            });
        }
        if (response.error && response.error.gqlError?.message == "Permission Denied.") {
          this.$router.push("/no-access");
        }
      },
    },
  },
};
</script>

<style scoped>
.max-width {
  width: 100%;
}

.exercise-dot {
  position: absolute;
  top: 16px;
  background-color: var(--v-exercise-base);
  border: 1px solid white;
  border-radius: 100%;
  width: 22px;
  height: 22px;
}
.block {
  display: block;
}
</style>
